"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useAuth } from "@/lib/auth-context";
import { EVENTS, referrerLabel, track } from "@/lib/analytics";
import { createStarterGarden, writeGuestGarden } from "@/lib/guest-garden";
import {
  TEMPLATES,
  buildStarterPlan,
  climateZoneFor,
  departmentFromPostalCode,
  type Level,
  type TemplateId,
} from "@/lib/garden/starter-plan";
import { zoneLabel } from "@/lib/catalog";

/**
 * Three questions, three screens.
 *
 * One decision per screen keeps the tap targets large on a phone — the persona
 * is someone standing in their garden. Every step can be skipped: an empty
 * canvas is still better than a form nobody finishes.
 */

const LEVELS: { id: Level; label: string; detail: string; emoji: string }[] = [
  {
    id: "debutant",
    label: "Je débute",
    detail: "Des cultures rapides et qui pardonnent les erreurs",
    emoji: "🌱",
  },
  {
    id: "confirme",
    label: "J'ai déjà cultivé",
    detail: "Tout le catalogue, y compris les cultures exigeantes",
    emoji: "🧑‍🌾",
  },
];

export function Onboarding() {
  const router = useRouter();
  const { enterGuestMode } = useAuth();

  const [step, setStep] = useState(0);
  const [template, setTemplate] = useState<TemplateId | null>(null);
  const [postalCode, setPostalCode] = useState("");
  const [creating, setCreating] = useState(false);

  const department = departmentFromPostalCode(postalCode);
  const zone = department ? climateZoneFor(department) : null;

  function start(garden: ReturnType<typeof createStarterGarden>, mode: string) {
    writeGuestGarden(garden);
    enterGuestMode();
    track(EVENTS.guestStarted, { from: referrerLabel(), mode });
    router.replace(`/dashboard/garden/${garden.id}`);
  }

  function finish(level: Level) {
    if (!template) return;
    setCreating(true);
    const plan = buildStarterPlan({ template, postalCode, level });
    track(EVENTS.onboardingCompleted, {
      template,
      level,
      zone: plan.climateZone,
      plants: plan.garden.placements.length,
    });
    start(plan.garden, "onboarding");
  }

  function skip() {
    track(EVENTS.onboardingSkipped, { step });
    start(createStarterGarden(), "vide");
  }

  return (
    <div className="w-full max-w-2xl">
      <ol className="flex items-center justify-center gap-2 mb-8" aria-label="Étapes">
        {[0, 1, 2].map((i) => (
          <li
            key={i}
            aria-current={i === step ? "step" : undefined}
            className={`h-1.5 rounded-full transition-all ${
              i === step
                ? "w-10 bg-primary"
                : i < step
                  ? "w-6 bg-garden-300"
                  : "w-6 bg-surface-container"
            }`}
          />
        ))}
      </ol>

      {step === 0 && (
        <Step
          title="Où cultivez-vous ?"
          subtitle="On dessine les bacs ou les planches à votre place — tout reste modifiable ensuite."
        >
          <div className="grid gap-3 sm:grid-cols-3">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  setTemplate(t.id);
                  setStep(1);
                }}
                className="card text-left hover:shadow-leaf-md hover:-translate-y-0.5 transition-all focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className="text-3xl block mb-2" aria-hidden>
                  {t.emoji}
                </span>
                <span className="block font-headline text-title-lg text-garden-800">
                  {t.label}
                </span>
                <span className="block text-body-md text-on-surface-variant mt-1">
                  {t.summary}
                </span>
              </button>
            ))}
          </div>
        </Step>
      )}

      {step === 1 && (
        <Step
          title="Dans quelle région ?"
          subtitle="Votre code postal détermine votre zone climatique — et donc vos vraies dates de semis."
        >
          <div className="max-w-xs mx-auto">
            <label
              htmlFor="postal"
              className="block text-label-md text-on-surface-variant mb-1"
            >
              Code postal
            </label>
            <input
              id="postal"
              name="postal"
              inputMode="numeric"
              autoComplete="postal-code"
              maxLength={5}
              placeholder="44000"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value.replace(/\D/g, ""))}
              className="input-field text-center text-headline-sm tracking-widest"
            />
            <p className="mt-3 text-body-md text-on-surface-variant min-h-[3rem]">
              {postalCode.length === 0 ? (
                "Cinq chiffres suffisent."
              ) : zone ? (
                <>
                  Département {department} —{" "}
                  <strong className="text-garden-800">
                    zone {zone}, {zoneLabel(zone)}
                  </strong>
                  .
                </>
              ) : (
                "Code postal incomplet."
              )}
            </p>
            <div className="flex gap-3 justify-center mt-2">
              <button type="button" onClick={() => setStep(0)} className="btn-ghost">
                Retour
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!zone}
                className="btn-primary !w-auto px-8 disabled:opacity-40"
              >
                Continuer
              </button>
            </div>
          </div>
        </Step>
      )}

      {step === 2 && (
        <Step
          title="Vous en êtes où ?"
          subtitle="Cela ne change que les cultures proposées au départ."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {LEVELS.map((l) => (
              <button
                key={l.id}
                type="button"
                disabled={creating}
                onClick={() => finish(l.id)}
                className="card text-left hover:shadow-leaf-md hover:-translate-y-0.5 transition-all disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-primary"
              >
                <span className="text-3xl block mb-2" aria-hidden>
                  {l.emoji}
                </span>
                <span className="block font-headline text-title-lg text-garden-800">
                  {l.label}
                </span>
                <span className="block text-body-md text-on-surface-variant mt-1">
                  {l.detail}
                </span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setStep(1)}
            className="btn-ghost mt-4"
            disabled={creating}
          >
            Retour
          </button>
        </Step>
      )}

      <div className="text-center mt-10 space-y-2">
        <button
          type="button"
          onClick={skip}
          className="text-body-md text-on-surface-variant underline underline-offset-2 hover:text-garden-700"
        >
          Passer et partir d&apos;un plan vide
        </button>
        <p className="text-label-md text-outline">
          Aucun compte nécessaire.{" "}
          <Link href="/auth/login" className="underline underline-offset-2">
            Déjà inscrit ?
          </Link>
        </p>
      </div>
    </div>
  );
}

function Step({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <section className="text-center">
      <h1 className="font-headline text-3xl md:text-display-md text-garden-900 mb-3">
        {title}
      </h1>
      <p className="text-body-lg text-on-surface-variant max-w-xl mx-auto mb-8">
        {subtitle}
      </p>
      {children}
    </section>
  );
}
